import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    IconDownload,
    IconId,
    IconPrinter,
    IconRefresh,
} from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import DatePickerField from '@/components/common/DatePickerField';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { recordToolUsage } from '../../../../../services/toolsService';
import ToolPageLayout from '../../components/common/ToolPageLayout';
import ToolPreviewPanel from '../../components/common/ToolPreviewPanel';
import {
    deriveToolStatus,
    getToolStatusText,
} from '../../toolPageUtils';
import { usePageZoom } from '../shared/usePagePreviewScale';
import MissingFieldsDialog from '../shared/MissingFieldsDialog';
import PagePreviewZoom from '../shared/PagePreviewZoom';
import UdyogPreviewFrame from './UdyogPreviewFrame';
import UdyogFormInfoBox from './UdyogFormInfoBox';
import UdyogAgeNoticeDialog from './UdyogAgeNoticeDialog';
import {
    ADDRESS_PARTS,
    COST_COUNT,
    INITIAL_UDYOG_FORM,
    LAST_DATE,
    MARITAL_LABELS,
    MONEY_KEYS,
    MONEY_PERIODS,
    STAGE_KEYS,
    ageOn,
    birthDateIssue,
    costTotal,
    displayField,
    formatDateField,
    getEmptyUdyogFields,
    getUdyogFormSnapshot,
    isUdyogFormComplete,
    normalizeMoney,
    parseDateField,
    saveUdyogFormSnapshot,
    toBengaliDigits,
} from './udyogFormEngine';
import { COST_HEADS } from './udyogMetrics';
import { downloadUdyogPdf, printUdyogForm } from './generateUdyogPdf';

const SLUG = 'general/udyog-application-form';

const inputClass = 'w-full rounded-xl border border-[#ECE8E4] bg-white px-3 py-2.5 text-[14px] text-[#3D3833] outline-none transition-colors focus:border-purple-400 focus:ring-2 focus:ring-purple-400/30 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100';

/** Calendar range: an applicant's birth date, and no future days. */
const TODAY = new Date();
const BIRTH_FROM_YEAR = 1950;

function FormLabel({ children, htmlFor }) {
    return (
        <label htmlFor={htmlFor} className="mb-1 block text-[12px] font-semibold text-[#6B635C] dark:text-zinc-400">
            {children}
        </label>
    );
}

function FormSection({ title, children }) {
    return (
        <div className="rounded-2xl border border-purple-200 bg-white px-4 py-4 dark:border-purple-900/40 dark:bg-zinc-900">
            <h3 className="mb-3.5 text-[13.5px] font-bold text-purple-700 dark:text-purple-300">{title}</h3>
            <div className="space-y-3">{children}</div>
        </div>
    );
}

/** A printed either/or, offered as a row of pills. */
function ChoiceGroup({ name, options, value, onChange }) {
    return (
        <div role="radiogroup" className="flex flex-wrap gap-2">
            {options.map(({ key, label }) => {
                const active = value === key;
                return (
                    <label
                        key={key}
                        className={`flex cursor-pointer items-center gap-2 rounded-xl border px-3 py-2 text-[13px] transition-colors ${active
                            ? 'border-purple-400 bg-purple-50 text-purple-800 dark:border-purple-600 dark:bg-purple-900/30 dark:text-purple-200'
                            : 'border-[#ECE8E4] text-[#3D3833] dark:border-zinc-700 dark:text-zinc-200'}`}
                    >
                        <input
                            type="radio"
                            name={name}
                            checked={active}
                            onChange={() => onChange(key)}
                            className="h-4 w-4 accent-purple-600"
                        />
                        {label}
                    </label>
                );
            })}
        </div>
    );
}

export default function UdyogFormGeneral() {
    const { t } = useTranslation();
    const previewRef = useRef(null);
    const pageZoom = usePageZoom();
    const snapshotTimerRef = useRef(null);

    const [form, setForm] = useState(INITIAL_UDYOG_FORM);
    const [focusField, setFocusField] = useState('');
    const [downloading, setDownloading] = useState(false);
    const [confirmingDownload, setConfirmingDownload] = useState(false);
    // Set when a birth date needs pointing out — on picking it, and again before
    // the form leaves as a PDF or a print.
    const [ageNotice, setAgeNotice] = useState(null);
    const [printing, setPrinting] = useState(false);
    const [hasSavedSnapshot, setHasSavedSnapshot] = useState(false);

    useEffect(() => {
        if (typeof window !== 'undefined') {
            setHasSavedSnapshot(!!getUdyogFormSnapshot());
        }
    }, []);

    const update = useCallback((key, value) => {
        setForm((prev) => {
            const next = { ...prev, [key]: value };
            window.clearTimeout(snapshotTimerRef.current);
            if (displayField(next.nameBn) || displayField(next.nameEn)) {
                snapshotTimerRef.current = window.setTimeout(() => {
                    saveUdyogFormSnapshot(next);
                    setHasSavedSnapshot(true);
                }, 400);
            }
            return next;
        });
    }, []);

    /**
     * Remember what was typed, then export — whatever state the form is in.
     * Missing fields do not block the download: printing it half-filled to finish
     * by hand is a normal way to use it. Downloading asks for a confirmation that
     * names what is still empty; printing does not.
     */
    const prepareForExport = () => {
        saveUdyogFormSnapshot(form);
        setHasSavedSnapshot(true);
    };

    const emptyFields = useMemo(() => getEmptyUdyogFields(form, t), [form, t]);

    const runDownloadPdf = async () => {
        prepareForExport();
        if (!previewRef.current) {
            notifications.show({ title: t('common.error'), message: t('udyogForm.errors.previewNotReady'), color: 'red' });
            return;
        }

        setDownloading(true);
        try {
            recordToolUsage(SLUG, { category: 'general' });
            const base = (form.nameEn || form.nameBn).trim().replace(/\s+/g, '-').slice(0, 40) || 'application';
            await downloadUdyogPdf(previewRef.current, `udyog-application-${base}.pdf`);
            notifications.show({ title: t('common.success'), message: t('udyogForm.downloadSuccess'), color: 'green' });
        } catch (err) {
            notifications.show({ title: t('common.error'), message: err?.message || t('udyogForm.errors.pdfFailed'), color: 'red' });
        } finally {
            setDownloading(false);
        }
    };

    const birthIssue = birthDateIssue(parseDateField(form.birthDate));

    const handleDownloadPdf = () => {
        if (birthIssue) {
            setAgeNotice('download');
            return;
        }
        if (emptyFields.length) {
            setConfirmingDownload(true);
            return;
        }
        runDownloadPdf();
    };

    const confirmDownloadPdf = () => {
        setConfirmingDownload(false);
        runDownloadPdf();
    };

    const handlePrint = () => {
        if (birthIssue) {
            setAgeNotice('print');
            return;
        }
        void runPrint();
    };

    const runPrint = async () => {
        recordToolUsage(SLUG, { category: 'general' });
        prepareForExport();
        if (!previewRef.current) {
            notifications.show({ title: t('common.error'), message: t('udyogForm.errors.previewNotReady'), color: 'red' });
            return;
        }

        setPrinting(true);
        try {
            await printUdyogForm(previewRef.current, t('udyogForm.errors.popupBlocked'));
            notifications.show({ title: t('udyogForm.printTitle'), message: t('udyogForm.printSuccess'), color: 'blue' });
        } catch (err) {
            notifications.show({ title: t('common.error'), message: err?.message || t('udyogForm.errors.printFailed'), color: 'red' });
        } finally {
            setPrinting(false);
        }
    };

    const handleClear = () => {
        setForm(INITIAL_UDYOG_FORM);
        notifications.show({ title: t('udyogForm.clearedTitle'), message: t('udyogForm.clearedMessage'), color: 'gray' });
    };

    const handleRestoreSnapshot = () => {
        const snapshot = getUdyogFormSnapshot();
        if (!snapshot) {
            notifications.show({ title: t('udyogForm.noSnapshotTitle'), message: t('udyogForm.noSnapshotMessage'), color: 'gray' });
            return;
        }
        setForm(snapshot);
        notifications.show({ title: t('udyogForm.restoredTitle'), message: t('udyogForm.restoredMessage'), color: 'green' });
    };

    // The part picker starts set, so it does not count as the user typing.
    const hasAnyInput = useMemo(
        () => Object.entries(form).some(([key, value]) => key !== 'businessStage' && displayField(value)),
        [form],
    );

    const isComplete = useMemo(() => isUdyogFormComplete(form, t), [form, t]);

    const status = deriveToolStatus({ hasInput: hasAnyInput, canRun: isComplete, hasResult: isComplete });

    const statusText = getToolStatusText(status, t, {
        ready: t('udyogForm.statusReady'),
        success: t('udyogForm.statusSuccess'),
    });

    const howToSteps = useMemo(
        () => [1, 2, 3, 4].map((i) => t(`udyogForm.howTo.step${i}`)),
        [t],
    );

    const age = ageOn(parseDateField(form.birthDate), LAST_DATE);
    const total = costTotal(form);

    const handleFieldChange = (key, value) => {
        update(key, MONEY_KEYS.has(key) ? normalizeMoney(value) : value);
    };

    const textField = (key, { label, ...extra } = {}) => (
        <div>
            <FormLabel htmlFor={key}>{label ?? t(`udyogForm.fields.${key}`)}</FormLabel>
            <input
                id={key}
                name={key}
                value={form[key]}
                onChange={(e) => handleFieldChange(key, e.target.value)}
                placeholder={t(`udyogForm.placeholders.${key}`, '')}
                inputMode={MONEY_KEYS.has(key) ? 'decimal' : undefined}
                className={inputClass}
                {...extra}
            />
        </div>
    );

    const areaField = (key, rows = 3) => (
        <div>
            <FormLabel htmlFor={key}>{t(`udyogForm.fields.${key}`)}</FormLabel>
            <textarea
                id={key}
                name={key}
                rows={rows}
                value={form[key]}
                onChange={(e) => handleFieldChange(key, e.target.value)}
                placeholder={t(`udyogForm.placeholders.${key}`, '')}
                className={`${inputClass} resize-y`}
            />
        </div>
    );

    /** Dates are picked, so they always come out দিন-মাস-বছর in Bengali digits. */
    const dateField = (key, { fromYear = 1950 } = {}) => (
        <div onFocusCapture={() => setFocusField(key)}>
            <FormLabel htmlFor={key}>{t(`udyogForm.fields.${key}`)}</FormLabel>
            <DatePickerField
                value={parseDateField(form[key])}
                onChange={(date) => {
                    update(key, formatDateField(date));
                    if (key === 'birthDate' && birthDateIssue(date)) setAgeNotice('picked');
                }}
                formatValue={formatDateField}
                placeholder={t(`udyogForm.placeholders.${key}`, '')}
                triggerClassName={inputClass}
                fromYear={fromYear}
                toYear={TODAY.getFullYear()}
                disabled={{ after: TODAY }}
                clearable
            />
        </div>
    );

    const addressFields = (prefix) => (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {ADDRESS_PARTS.map((part) => (
                <div key={part}>
                    {textField(`${prefix}${part}`, {
                        label: t(`udyogForm.addressParts.${part}`),
                        placeholder: t(`udyogForm.addressParts.${part}`),
                    })}
                </div>
            ))}
        </div>
    );

    const moneyRow = (prefix) => (
        <div>
            <FormLabel>{t(`udyogForm.fields.${prefix}`)}</FormLabel>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                {MONEY_PERIODS.map((period) => (
                    <div key={period}>
                        {textField(`${prefix}${period}`, {
                            label: t(`udyogForm.periods.${period}`),
                            placeholder: t('udyogForm.placeholders.money'),
                        })}
                    </div>
                ))}
            </div>
        </div>
    );

    const running = form.businessStage === 'running';

    return (
        <>
        <UdyogAgeNoticeDialog
            open={Boolean(ageNotice)}
            birthDate={form.birthDate}
            confirmLabel={ageNotice === 'download'
                ? t('underageNotice.confirmDownload')
                : ageNotice === 'print' ? t('underageNotice.confirmPrint') : undefined}
            onCancel={() => setAgeNotice(null)}
            onConfirm={() => {
                const mode = ageNotice;
                setAgeNotice(null);
                if (mode === 'print') { void runPrint(); return; }
                if (mode !== 'download') return;
                if (emptyFields.length) setConfirmingDownload(true);
                else runDownloadPdf();
            }}
        />
        <MissingFieldsDialog
            open={confirmingDownload}
            fields={emptyFields}
            loading={downloading}
            onCancel={() => setConfirmingDownload(false)}
            onConfirm={confirmDownloadPdf}
        />
        <ToolPageLayout
            slug={SLUG}
            subtitleKey="udyogForm.subtitle"
            variant="wideControls"
            railWidthClass="lg:w-[calc(70%_-_9px)]"
            status={status}
            statusText={statusText}
            controlsTitle={t('udyogForm.settingsTitle')}
            howToSteps={howToSteps}
            onReset={hasAnyInput ? handleClear : undefined}
            resetLabel={t('udyogForm.clearAll')}
            primaryAction={{
                label: t('udyogForm.downloadPdf'),
                onClick: handleDownloadPdf,
                disabled: printing,
                loading: downloading,
                icon: IconDownload,
            }}
            secondaryAction={[
                hasSavedSnapshot ? {
                    key: 'restore',
                    label: t('udyogForm.restoreLast'),
                    onClick: handleRestoreSnapshot,
                    disabled: downloading || printing,
                    icon: IconRefresh,
                } : null,
                {
                    key: 'print',
                    label: printing ? t('toolPage.processing') : t('udyogForm.print'),
                    onClick: handlePrint,
                    disabled: downloading || printing,
                    icon: IconPrinter,
                },
            ]}
            controls={(
                <div
                    className="space-y-3 font-[Kalpurush,'Noto_Sans_Bengali',sans-serif]"
                    onFocusCapture={(e) => { if (e.target.name) setFocusField(e.target.name); }}
                >
                    <FormSection title={t('udyogForm.sections.personal')}>
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            {textField('nameBn', { autoComplete: 'name' })}
                            {textField('nameEn', { className: `${inputClass} uppercase` })}
                            {textField('fatherName')}
                            {textField('motherName')}
                            {textField('nid', { inputMode: 'numeric' })}
                            {dateField('birthDate', { fromYear: BIRTH_FROM_YEAR })}
                        </div>
                        {age ? (
                            <p className="rounded-lg bg-purple-50 px-3 py-2 text-[12.5px] text-purple-800 dark:bg-purple-900/20 dark:text-purple-200">
                                {t('udyogForm.ageResult', {
                                    years: toBengaliDigits(age.years),
                                    months: toBengaliDigits(age.months),
                                    days: toBengaliDigits(age.days),
                                    date: t('udyogForm.closingDate'),
                                })}
                            </p>
                        ) : null}
                        <div>
                            <FormLabel>{t('udyogForm.fields.maritalStatus')}</FormLabel>
                            <ChoiceGroup
                                name="maritalStatus"
                                value={form.maritalStatus}
                                onChange={(v) => update('maritalStatus', v)}
                                options={Object.keys(MARITAL_LABELS).map((key) => ({ key, label: t(`udyogForm.marital.${key}`) }))}
                            />
                        </div>
                        {form.maritalStatus === 'married' ? (
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                {textField('spouseName')}
                                {textField('spouseNid', { inputMode: 'numeric' })}
                            </div>
                        ) : null}
                        {textField('education')}
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            {textField('phone', { inputMode: 'tel', autoComplete: 'tel' })}
                            {textField('phoneAlt', { inputMode: 'tel' })}
                        </div>
                        {textField('email', { type: 'email', autoComplete: 'email' })}
                        {areaField('training')}
                        {areaField('experience')}
                        {areaField('bankAccount', 2)}
                        {areaField('existingLoan', 2)}
                    </FormSection>

                    <FormSection title={t('udyogForm.sections.address')}>
                        <p className="text-[12.5px] font-semibold text-[#3D3833] dark:text-zinc-200">{t('udyogForm.fields.presentAddress')}</p>
                        {addressFields('present')}
                        <p className="text-[12.5px] font-semibold text-[#3D3833] dark:text-zinc-200">{t('udyogForm.fields.permanentAddress')}</p>
                        <label className="flex cursor-pointer items-center gap-2 text-[13px] text-[#3D3833] dark:text-zinc-200">
                            <input
                                type="checkbox"
                                checked={Boolean(form.sameAddress)}
                                onChange={(e) => update('sameAddress', e.target.checked ? 'yes' : '')}
                                className="h-4 w-4 accent-purple-600"
                            />
                            {t('udyogForm.sameAddress')}
                        </label>
                        {form.sameAddress ? null : addressFields('permanent')}
                    </FormSection>

                    <FormSection title={t('udyogForm.sections.business')}>
                        <div>
                            <FormLabel>{t('udyogForm.fields.businessStage')}</FormLabel>
                            <ChoiceGroup
                                name="businessStage"
                                value={form.businessStage}
                                onChange={(v) => update('businessStage', v)}
                                options={STAGE_KEYS.map((key) => ({ key, label: t(`udyogForm.stages.${key}`) }))}
                            />
                        </div>
                        {running ? (
                            <>
                                {textField('bizName')}
                                {areaField('bizType', 2)}
                                {dateField('bizStartDate', { fromYear: 1970 })}
                                {areaField('tradeLicense', 2)}
                                {areaField('avgPrice', 2)}
                                {moneyRow('bizSales')}
                                {moneyRow('bizProfit')}
                                {areaField('monthlyCost', 2)}
                                {areaField('involvement', 2)}
                                {textField('totalInvestment')}
                                {areaField('bizRawMaterial', 2)}
                                {textField('marketingArea')}
                                {areaField('bizMarketPosition')}
                                {areaField('bizRisks')}
                                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                    {textField('staffTotal', { inputMode: 'numeric' })}
                                    {textField('staffFemale', { inputMode: 'numeric' })}
                                </div>
                                {areaField('staffNote', 2)}
                                {areaField('expansionPlan')}
                            </>
                        ) : (
                            <>
                                {areaField('businessIdea', 6)}
                                {textField('buyers')}
                                {textField('startTimeline')}
                                {areaField('businessType', 2)}
                                {areaField('businessLocation', 2)}
                                {areaField('productType', 2)}
                                {textField('rawMaterial')}
                                {textField('productMarket')}
                                {moneyRow('sales')}
                                {moneyRow('profit')}
                                {areaField('risks')}
                                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                    {textField('jobsTotal', { inputMode: 'numeric' })}
                                    {textField('jobsFemale', { inputMode: 'numeric' })}
                                </div>
                                {areaField('jobsNote', 2)}
                                {areaField('marketPosition')}
                            </>
                        )}
                    </FormSection>

                    <FormSection title={t('udyogForm.sections.financing')}>
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                            {textField('capitalRequired', { placeholder: t('udyogForm.placeholders.money') })}
                            {textField('ownInvestment', { placeholder: t('udyogForm.placeholders.money') })}
                            {textField('financingRequested', { placeholder: t('udyogForm.placeholders.money') })}
                        </div>
                        <p className="text-[12.5px] font-semibold text-[#3D3833] dark:text-zinc-200">{t('udyogForm.costTitle')}</p>
                        {Array.from({ length: COST_COUNT }, (_, i) => i + 1).map((n) => (
                            <div key={n}>
                                <p className="mb-1 text-[12px] font-semibold text-[#6B635C] dark:text-zinc-400">
                                    {toBengaliDigits(n)}. {COST_HEADS[n - 1]}
                                </p>
                                <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                                    <input
                                        name={`cost${n}Detail`}
                                        aria-label={t('udyogForm.costColumns.detail')}
                                        value={form[`cost${n}Detail`]}
                                        onChange={(e) => handleFieldChange(`cost${n}Detail`, e.target.value)}
                                        placeholder={t('udyogForm.costColumns.detail')}
                                        className={inputClass}
                                    />
                                    <input
                                        name={`cost${n}Amount`}
                                        aria-label={t('udyogForm.costColumns.amount')}
                                        value={form[`cost${n}Amount`]}
                                        onChange={(e) => handleFieldChange(`cost${n}Amount`, e.target.value)}
                                        placeholder={t('udyogForm.costColumns.amount')}
                                        inputMode="decimal"
                                        className={inputClass}
                                    />
                                    <input
                                        name={`cost${n}Source`}
                                        aria-label={t('udyogForm.costColumns.source')}
                                        value={form[`cost${n}Source`]}
                                        onChange={(e) => handleFieldChange(`cost${n}Source`, e.target.value)}
                                        placeholder={t('udyogForm.costColumns.source')}
                                        className={inputClass}
                                    />
                                </div>
                            </div>
                        ))}
                        {total ? (
                            <p className="rounded-lg bg-purple-50 px-3 py-2 text-[12.5px] text-purple-800 dark:bg-purple-900/20 dark:text-purple-200">
                                {t('udyogForm.costTotal', { total })}
                            </p>
                        ) : null}
                    </FormSection>

                    <FormSection title={t('udyogForm.sections.other')}>
                        {textField('disability')}
                        {textField('ethnicMinority')}
                        {textField('otherInfo')}
                        {dateField('applicationDate', { fromYear: TODAY.getFullYear() - 1 })}
                    </FormSection>
                </div>
            )}
            preview={(
                <div className="flex flex-col gap-3.5 lg:sticky lg:top-4">
                    <ToolPreviewPanel
                        title={t('udyogForm.previewTitle')}
                        status={status}
                        statusLabel={t(`toolPage.status.${status}`)}
                        idleTitle={t('udyogForm.previewEmpty')}
                        idleHint={t('udyogForm.previewEmptyHint')}
                        showIdle={false}
                        tall
                        flushContent
                        headerAction={<PagePreviewZoom {...pageZoom} />}
                    >
                        <div className="flex h-[min(1100px,calc(100vh-186px))] min-h-[420px] flex-col">
                            <div className="mb-2 flex items-center gap-1.5 text-[12px] font-semibold text-[#6B635C] dark:text-zinc-400">
                                <IconId size={14} stroke={1.8} />
                                {t('udyogForm.livePreview')}
                            </div>
                            <UdyogPreviewFrame form={form} previewRef={previewRef} zoom={pageZoom.zoom} focusField={focusField} />
                        </div>
                    </ToolPreviewPanel>
                </div>
            )}
            belowWorkArea={<UdyogFormInfoBox />}
        />
        </>
    );
}
